import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from '@pharvio/accounts/admin-account/data-access';
import {
  AsyncDataItem,
  DateAdapterService,
  IOrder,
  IOrderQueryParams,
  IPaginationMeta,
  ORDER_STATUS_OPTIONS,
  OrderSource,
  OrderStatus,
  IOutboxRecord,
  OfflineModeService
} from '@pharvio/core/shared';
import {
  AlertService,
  AmountToMajorPipe,
  ClickStopPropagationDirective,
  HasPermissionPipe,
  LayoutToolbarComponent,
  PaginatorComponent,
  PricePipe,
  RemoveCharacterPipe,
  SharedSidePanel,
  SidePanelDirective
} from '@pharvio/core/ui';
import {
  CancelOrder,
  CompletePacking,
  LoadOrders,
  OrdersState,
  SelectOrder,
  StartPacking,
  VerifyOrder
} from '@pharvio/orders/data-access';
import { OrdersTableComponent } from '@pharvio/orders/ui';
import {
  CancelLocalOrder,
  LoadLocalOrders,
  mapOutboxRecordToOrder,
  OfflineDialogService,
  offlineQrStatus,
  OfflineState,
  ReplayOutbox
} from '@pharvio/offline';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { PERMISSIONS } from '../../../../core/constants/permissions.constant';
import { OrderPaths } from '../../../../core/constants/routes/orders-routes';

/**
 * Maps each status to the next logical action a staff member can take.
 * Statuses not in this map have no "advance" action.
 */
const NEXT_ACTION: Partial<
  Record<OrderStatus, { label: string; action: string }>
> = {
  payment_received: { label: 'Verify Payment', action: 'verify' },
  confirmed: { label: 'Start Packing', action: 'pack' },
  packing: { label: 'Complete Packing', action: 'complete_packing' }
};

@Component({
  selector: 'app-orders-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatDividerModule,
    SharedSidePanel,
    SidePanelDirective,
    ClickStopPropagationDirective,
    PricePipe,
    AmountToMajorPipe,
    OrdersTableComponent,
    PaginatorComponent,
    LayoutToolbarComponent,
    RouterLink,
    HasPermissionPipe,
    RemoveCharacterPipe
  ],
  templateUrl: './orders-list.component.html'
})
export class OrdersListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly alertService = inject(AlertService);
  private readonly dateService = inject(DateAdapterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly offlineMode = inject(OfflineModeService);
  private readonly offlineDialog = inject(OfflineDialogService);

  /** Offline: server rows are read-only and local (unsynced) orders are listed first. */
  readonly isOffline = this.offlineMode.isOffline;

  /** Outbox rows rendered with the shared orders table. */
  readonly localOrders$: Observable<AsyncDataItem<IOrder[]>> = this.store
    .select(OfflineState.localOrders)
    .pipe(
      map((item) => ({
        ...item,
        data: item.data
          .filter((r: IOutboxRecord) => r.status !== 'synced')
          .map(mapOutboxRecordToOrder)
      }))
    );
  readonly localRecords$: Observable<IOutboxRecord[]> = this.store.select(
    OfflineState.localOrders
  ).pipe(map((item) => item.data));

  readonly permissions$ = this.store.select(AuthState.permissions);
  readonly PERMISSIONS = PERMISSIONS;
  readonly orderPaths = OrderPaths;
  readonly today = new Date();

  readonly orders$: Observable<AsyncDataItem<IOrder[]>> = this.store.select(
    OrdersState.orders
  );
  readonly selectedId$: Observable<string | null> = this.store.select(
    OrdersState.selectedId
  );
  readonly paginationMeta$: Observable<IPaginationMeta | null> =
    this.store.select(OrdersState.paginationMeta);

  // readonly statusFilterCtrl = new FormControl<OrderStatus | ''>('');
  readonly statusOptions = ORDER_STATUS_OPTIONS;
  readonly sourceOptions: { value: OrderSource | ''; label: string }[] = [
    { value: '', label: 'All sources' },
    { value: 'pos_cash', label: 'POS Cash' },
    { value: 'pos_pay_by_link', label: 'POS Pay by Link' },
    { value: 'pharmacist_initiated', label: 'Pharmacist Initiated' },
    { value: 'customer_self_serve', label: 'Customer Self Serve' }
  ];

  @ViewChild(SidePanelDirective) private sidePanelDir?: SidePanelDirective;

  private currentPage = 1;

  readonly filterForm = this.fb.group({
    from: [null as Date | null],
    to: [null as Date | null],
    // '' is the "All" option in ORDER_STATUS_OPTIONS
    status: [null as OrderStatus | '' | null],
    source: ['' as OrderSource | '' | null],
    search: ['' as string | null]
  });

  ngOnInit(): void {
    // Seed from query params
    const qp = this.route.snapshot.queryParams;
    const initialStatus = (qp['status'] as OrderStatus) || '';
    const initialPage = parseInt(qp['page'], 10) || 1;

    this.filterForm.controls.status.setValue(initialStatus, {
      emitEvent: false
    });
    this.filterForm.controls.source.setValue(
      (qp['source'] as OrderSource) || '',
      { emitEvent: false }
    );
    this.filterForm.controls.search.setValue(qp['search'] || '', {
      emitEvent: false
    });
    this.filterForm.controls.from.setValue(this.toDate(qp['from']), {
      emitEvent: false
    });
    this.filterForm.controls.to.setValue(this.toDate(qp['to']), {
      emitEvent: false
    });
    this.currentPage = initialPage;

    this.store.dispatch(new LoadLocalOrders());
    this.loadOrders();

    // Any filter change → reset to page 1, update URL
    // Debounced so typing in the search box doesn't fire a request per key;
    // distinct so a no-op edit (e.g. trailing space) doesn't reload.
    this.filterForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (a, b) => JSON.stringify(a) === JSON.stringify(b)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.updateQueryParams();
        this.loadOrders();
      });
  }

  /** True while any filter is set. */
  get hasFilters(): boolean {
    const { from, to, status, source, search } = this.filterForm.value;
    return !!(from || to || status || source || search?.trim());
  }

  /** Drops every filter — the single reset emits one reload, not three. */
  clearFilters(): void {
    this.filterForm.reset({
      from: null,
      to: null,
      status: '',
      source: '',
      search: ''
    });
  }

  reload(): void {
    this.loadOrders(true);
  }

  onPage(page: number): void {
    this.currentPage = page;
    this.updateQueryParams();
    this.loadOrders();
  }

  onOrderClick(order: IOrder): void {
    this.select(order.id);
    this.sidePanelDir?.openSidePanel();
  }

  viewOrder(id: string): void {
    this.router.navigate([OrderPaths.Detail(id)]);
  }

  select(id: string): void {
    this.store.dispatch(new SelectOrder(id));
  }

  deselect(): void {
    this.store.dispatch(new SelectOrder(null));
  }

  selectedOrder(orders: IOrder[], id: string | null): IOrder | null {
    return orders.find((o) => o.id === id) ?? null;
  }

  nextLabel(status: OrderStatus): string | null {
    return NEXT_ACTION[status]?.label ?? null;
  }

  advance(order: IOrder): void {
    const next = NEXT_ACTION[order.status];
    if (!next) return;

    switch (next.action) {
      case 'verify':
        this.store
          .dispatch(new VerifyOrder(order.id, {}))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.alertService.success('Payment verified.'),
            error: () => this.alertService.error("Couldn't verify payment.")
          });
        break;
      case 'pack':
        this.store
          .dispatch(new StartPacking(order.id))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.alertService.success('Packing started.'),
            error: () => this.alertService.error("Couldn't start packing.")
          });
        break;
      case 'complete_packing':
        this.store
          .dispatch(new CompletePacking(order.id, {}))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.alertService.success('Packing completed.'),
            error: () => this.alertService.error("Couldn't complete packing.")
          });
        break;
    }
  }

  cancel(order: IOrder): void {
    this.alertService
      .showConfirmationModal({
        title: 'Cancel Order',
        message: `Cancel order ${order.orderNumber}?`,
        primaryButton: { title: 'Cancel Order', value: true },
        secondaryButton: { title: 'No', value: false }
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store
            .dispatch(
              new CancelOrder(order.id, { reason: 'Cancelled by staff' })
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.alertService.success('Order cancelled.'),
              error: () => this.alertService.error("Couldn't cancel order.")
            });
        }
      });
  }

  // ── Local (unsynced) orders ────────────────────────────────────────────

  syncNow(): void {
    if (this.isOffline()) {
      this.alertService.info('Connect to the internet to sync sales.');
      return;
    }
    const before = this.store.selectSnapshot(OfflineState.lastReplayReport);
    this.store
      .dispatch(new ReplayOutbox())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const after = this.store.selectSnapshot(OfflineState.lastReplayReport);
        if (after && after !== before) {
          this.offlineDialog.openReplayReport(after);
        } else {
          this.alertService.info('Nothing to sync right now.');
        }
      });
  }

  /** A local QR order the customer can still scan and pay. */
  qrPayable(records: IOutboxRecord[], order: IOrder): boolean {
    const record = records.find((r) => r.clientOrderId === order.id);
    return !!record && offlineQrStatus(record) === 'payable';
  }

  showQr(records: IOutboxRecord[], order: IOrder): void {
    const record = records.find((r) => r.clientOrderId === order.id);
    if (!record || !this.offlineDialog.openQrForRecord(record)) {
      this.alertService.warning('This QR code has expired. Create the order again.');
    }
  }

  /** Never sent, rejected by the server, or an issued QR: cancel releases the local stock. */
  canCancelLocal(records: IOutboxRecord[], order: IOrder): boolean {
    const record = records.find((r) => r.clientOrderId === order.id);
    return (
      !!record &&
      (record.status === 'pending' || record.status === 'failed' || record.status === 'issued')
    );
  }

  cancelLocal(order: IOrder): void {
    this.alertService
      .showConfirmationModal({
        title: 'Cancel offline order',
        message: `Cancel ${order.orderNumber}? It has not been sent to the server and will be discarded.`,
        primaryButton: { title: 'Cancel Order', value: true },
        secondaryButton: { title: 'Keep', value: false }
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.store
          .dispatch(new CancelLocalOrder(order.id))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.alertService.success('Offline order cancelled.'),
            error: () => this.alertService.error("Couldn't cancel the offline order.")
          });
      });
  }

  private loadOrders(force = false): void {
    const status = this.filterForm.controls.status.value || undefined;
    const source = this.filterForm.controls.source.value || undefined;
    const search = this.filterForm.controls.search.value?.trim() || undefined;
    const { from, to } = this.dateRange();
    const params: IOrderQueryParams = {
      ...(status ? { status } : {}),
      ...(source ? { source } : {}),
      ...(search ? { search } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      page: this.currentPage
    };
    this.store.dispatch(new LoadOrders(params, force));
  }

  private updateQueryParams(): void {
    const status = this.filterForm.controls.status.value || undefined;
    const source = this.filterForm.controls.source.value || undefined;
    const search = this.filterForm.controls.search.value?.trim() || undefined;
    const { from, to } = this.dateRange();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        status: status || null,
        source: source ?? null,
        search: search ?? null,
        from: from ?? null,
        to: to ?? null,
        page: this.currentPage > 1 ? this.currentPage : null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * The picked dates as ISO strings — widened to whole days so the range covers
   * every order created on the `from` and `to` dates.
   */
  private dateRange(): { from?: string; to?: string } {
    const { from, to } = this.filterForm.value;
    return {
      from: from
        ? this.dateService
            .addPeriod({ date: from, amount: 0, unit: 'day' })
            .startOf('day')
            .toString()
            .split('T')[0]
        : undefined,
      to: to
        ? this.dateService
            .addPeriod({ date: to, amount: 0, unit: 'day' })
            .endOf('day')
            .toString()
            .split('T')[0]
        : undefined
    };
  }

  private toDate(value: string | undefined): Date | null {
    return value ? new Date(value) : null;
  }
}
